import { mapEventTimesToUpdateInput } from '@griply/services';
import {
  ActionEvents,
  CalendarEventGuestNotification,
  CalendarEventOccurrenceTarget,
  ExternalConnection,
  ExternalConnectionsProviders,
} from '@griply/types';
import {
  getConnectionProvider,
  getExternalEventDragQuestions,
} from '@griply/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { queryKey as externalEventsQueryKey } from 'shared/hooks/queries/use-external-calendar-events-query';
import { useFetchExternalCalendarEvent } from 'shared/hooks/queries/use-fetch-external-calendar-event';
import { useUpdateExternalCalendarEventMutation } from 'shared/hooks/queries/use-update-external-calendar-event-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { ExternalEventDragRequest } from 'shared/lib/full-calendar';

export type PendingDrag = {
  request: ExternalEventDragRequest;
  revert: () => void;
  // from the same detail fetch that answered `isRecurring` — no second round trip
  asksAboutGuests: boolean;
};

// the occurrence answer, held while the guest question is on screen
export type PendingNotify = PendingDrag & {
  occurrenceTarget: CalendarEventOccurrenceTarget;
};

export type BlockedMove = {
  provider: ExternalConnectionsProviders;
  url: string | undefined;
};

export type UseEventDragFlowOptions = {
  connections: ExternalConnection[];
  editAllowed: boolean;
  // shared with the popup's submit path, so the provider owns it and passes it down
  update: ReturnType<typeof useUpdateExternalCalendarEventMutation>;
};

export type EventDragFlow = {
  pendingDrag: PendingDrag | null;
  pendingNotify: PendingNotify | null;
  blockedMove: BlockedMove | null;
  onDragEvent: (
    request: ExternalEventDragRequest,
    revert: () => void,
  ) => Promise<void>;
  onSelectDragOccurrences: (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => void;
  onCancelDragOccurrences: () => void;
  onSelectDragNotify: (
    guestNotification: CalendarEventGuestNotification,
  ) => void;
  onCancelDragNotify: () => void;
  closeBlockedMove: () => void;
};

export const useEventDragFlow = ({
  connections,
  editAllowed,
  update,
}: UseEventDragFlowOptions): EventDragFlow => {
  const [pendingDrag, setPendingDrag] = useState<PendingDrag | null>(null);
  const [pendingNotify, setPendingNotify] = useState<PendingNotify | null>(
    null,
  );
  const [blockedMove, setBlockedMove] = useState<BlockedMove | null>(null);

  const track = useTrackEvents();
  const queryClient = useQueryClient();
  const openPremiumDialog = useOpenPremiumDialog();
  const fetchEventDetails = useFetchExternalCalendarEvent();

  const writeDrag = useCallback(
    (
      request: ExternalEventDragRequest,
      occurrenceTarget: CalendarEventOccurrenceTarget,
      guestNotification: CalendarEventGuestNotification | null,
    ) => {
      update.mutate({
        payload: mapEventTimesToUpdateInput(request.eventRef, {
          allDay: request.allDay,
          start: request.start,
          end: request.end,
          occurrenceTarget,
          // the grid paints in the browser's zone, so that is the zone its boundaries are in
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          guestNotification,
        }),
        optimistic: request.optimistic,
      });
      track(ActionEvents.CalendarEventUpdate, {
        provider: getConnectionProvider(
          connections,
          request.eventRef.connectionId,
        ),
        all_day: request.allDay,
        has_guests: !!guestNotification,
        has_video_call: false,
        is_recurring: request.isRecurring,
        edit_scope: occurrenceTarget,
        notified_guests: guestNotification
          ? guestNotification === CalendarEventGuestNotification.All
          : null,
      });
    },
    [connections, track, update],
  );

  const onDragEvent = useCallback(
    async (request: ExternalEventDragRequest, revert: () => void) => {
      // Refused before anything is awaited, so the event snaps back under the pointer.
      if (!editAllowed) {
        revert();
        openPremiumDialog();
        return;
      }

      // Only an explicit `false` refuses: an older backend leaves the flag absent and the
      // write fails at the provider as before.
      if (request.isOrganizer === false) {
        revert();
        setBlockedMove({
          provider: getConnectionProvider(
            connections,
            request.eventRef.connectionId,
          ),
          url: request.url,
        });
        return;
      }

      // one fetch answers both questions; a failure falls through to the list payload's answer
      const details = await fetchEventDetails(request.eventRef).catch(
        () => null,
      );
      const { asksOccurrence, asksAboutGuests } = getExternalEventDragQuestions(
        {
          provider: getConnectionProvider(
            connections,
            request.eventRef.connectionId,
          ),
          details,
          listIsRecurring: request.isRecurring,
        },
      );

      if (asksOccurrence) {
        setPendingDrag({ request, revert, asksAboutGuests });
        return;
      }
      if (asksAboutGuests) {
        setPendingNotify({
          request,
          revert,
          asksAboutGuests,
          occurrenceTarget: CalendarEventOccurrenceTarget.Single,
        });
        return;
      }
      writeDrag(request, CalendarEventOccurrenceTarget.Single, null);
    },
    [connections, editAllowed, openPremiumDialog, fetchEventDetails, writeDrag],
  );

  const closeBlockedMove = useCallback(() => setBlockedMove(null), []);

  const onSelectDragOccurrences = useCallback(
    (occurrenceTarget: CalendarEventOccurrenceTarget) => {
      setPendingDrag(null);
      if (!pendingDrag) {
        return;
      }
      if (pendingDrag.asksAboutGuests) {
        setPendingNotify({ ...pendingDrag, occurrenceTarget });
        return;
      }
      writeDrag(pendingDrag.request, occurrenceTarget, null);
    },
    [pendingDrag, writeDrag],
  );

  const onSelectDragNotify = useCallback(
    (guestNotification: CalendarEventGuestNotification) => {
      setPendingNotify(null);
      if (!pendingNotify) {
        return;
      }
      writeDrag(
        pendingNotify.request,
        pendingNotify.occurrenceTarget,
        guestNotification,
      );
    },
    [pendingNotify, writeDrag],
  );

  // Nothing was written, so undo FullCalendar's move; the refetch backstops a stale EventApi.
  const revertDrag = useCallback(
    (pending: PendingDrag | null) => {
      try {
        pending?.revert();
      } catch {
        // stale FullCalendar EventImpl — the refetch below restores the server position
      }
      queryClient.invalidateQueries({ queryKey: [externalEventsQueryKey] });
    },
    [queryClient],
  );

  const onCancelDragOccurrences = useCallback(() => {
    revertDrag(pendingDrag);
    setPendingDrag(null);
  }, [pendingDrag, revertDrag]);

  // backing out of the guest question abandons the move too — nothing has been written yet
  const onCancelDragNotify = useCallback(() => {
    revertDrag(pendingNotify);
    setPendingNotify(null);
  }, [pendingNotify, revertDrag]);

  return {
    pendingDrag,
    pendingNotify,
    blockedMove,
    onDragEvent,
    onSelectDragOccurrences,
    onCancelDragOccurrences,
    onSelectDragNotify,
    onCancelDragNotify,
    closeBlockedMove,
  };
};
